import merge from 'lodash/merge';
import AddIcon from '@mui/icons-material/Add';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid2, Stack, Paper, Alert, Typography, Button } from '@mui/material';
import { FieldValues, useForm, useFieldArray, Control, FieldPath } from 'react-hook-form';

import { usePageTitle } from '@enroller/utils';
import {
  FormField,
  FormFieldType,
  API_FIELDS_APPLICATION as APPLICATION,
  API_FIELDS_APPLICATION_LANGUAGE as LANGUAGE,
  PUT_API_FIELDS_APPLICATION as PUT_APPLICATION,
} from '@enroller/admissions-components';

import { TitledSection } from '../../../layout/TitledSection';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import {
  defaultValues,
  schoolAndLanguagesSchema,
  SchoolAndLanguagesFormValues,
  createStudentApplicationPayload,
  SCHOOL_LANGUAGES_PAGE_SECTIONS,
} from './SchoolAndLanguagesFormValues';

import { useApplicationContext } from '../../../contexts/ApplicationContext/ApplicationContext';

import type { StudentApplicationValues } from '../StudentApplication.ts';

const typeOfSchoolOptions = [
  {
    label: 'Public',
    value: 'Public',
  },
  {
    label: 'Private',
    value: 'Private',
  },
  {
    label: 'Boarding',
    value: 'Boarding',
  },
  {
    label: 'Other',
    value: 'Other',
    textField: { label: '', name: `${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.typeOfSchool}.other`, placeholder: 'Please explain' },
  },
];

const gradeLevelOptions = [
  {
    label: '8th',
    value: '8th',
  },
  {
    label: '9th',
    value: '9th',
  },
  {
    label: '10th',
    value: '10th',
  },
  {
    label: '11th',
    value: '11th',
  },
  {
    label: '12th',
    value: '12th',
  },
];

// Make sure values are in the same order as in the CRM
const graduationSituationOptions = [
  {
    label: 'I will have graduated before coming to the U.S.',
    value: 'I will have graduated before coming to the U.S.',
  },
  {
    label: 'I will be graduating after I return to my home country.',
    value: 'I will be graduating after I return to my home country',
  },
];

interface SchoolAndLanguagesFormProps {
  modulesID?: StudentApplicationValues['modulesID'];
  schoolAndLanguages?: SchoolAndLanguagesFormValues;
  submitApplication: (data: FieldValues) => Promise<void>;
}

export const SchoolAndLanguages = ({ schoolAndLanguages, submitApplication, modulesID }: SchoolAndLanguagesFormProps) => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');
  const { setApplicationPageData, displayErrors } = useApplicationContext();

  useEffect(() => {
    setHeaderTitle({ title: 'School & Languages', subtitle: 'Your School' });
    setPageTitle('School & Languages');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(
    () => zodResolver(validationMode === ValidationType.Full ? schoolAndLanguagesSchema.required : schoolAndLanguagesSchema.optional),
    [validationMode],
  );

  const { control, handleSubmit, reset, trigger } = useForm<SchoolAndLanguagesFormValues>({
    defaultValues,
    resolver,
  });

  // Trigger errors if they have been triggered in another form
  useEffect(() => {
    if (displayErrors) setValidationMode(ValidationType.Full);
  }, [displayErrors, trigger]);

  useEffect(() => {
    if (schoolAndLanguages) {
      reset(merge({}, defaultValues, schoolAndLanguages));
    }
  }, [schoolAndLanguages, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        if (!modulesID) return;
        setApplicationPageData('schoolAndLanguages', data);
        setIsFormLoading(true);

        submitApplication(createStudentApplicationPayload(data, modulesID))
          .then(() => console.log('[SAP] School And Languages API: submit success'))
          .catch((error) => console.error('[SAP] School And Languages API: submit error:', error))
          .finally(() => setIsFormLoading(false));
      })(),
    [handleSubmit, modulesID, setIsFormLoading, submitApplication, setApplicationPageData],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <TitledSection title="Your School">
        <Grid2 size={12}>
          <FormField
            control={control}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.typeOfSchool}.value`}
            type={FormFieldType.Radio}
            options={typeOfSchoolOptions}
            label="Type of school you attend"
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            control={control}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.gradeLevel}`}
            type={FormFieldType.Radio}
            options={gradeLevelOptions}
            label="TCurrent grade level"
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            control={control}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.highestGradLevelSchool}`}
            type={FormFieldType.Radio}
            options={gradeLevelOptions}
            label="Highest grade level in your secondary / high school"
          />
        </Grid2>

        <Grid2 size={12}>
          <FormField
            control={control}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.graduationSituation}`}
            type={FormFieldType.Radio}
            options={graduationSituationOptions}
            label="Please choose the option that best describes your situation"
          />
        </Grid2>
        <Grid2 size={4}>
          <FormField
            control={control}
            label="High School Graduation Date"
            type={FormFieldType.Date}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.school}.${APPLICATION.graduationDate}`}
          />
        </Grid2>
      </TitledSection>

      <TitledSection title="Languages">
        <Grid2 size={{ xs: 12, md: 4 }}>
          <FormField
            control={control}
            label="Years of English"
            type={FormFieldType.Number}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.languages}.${APPLICATION.yearsOfEnglish}`}
            textSuffix="years"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <FormField
            control={control}
            label="What is your native language?"
            type={FormFieldType.Text}
            name={`${SCHOOL_LANGUAGES_PAGE_SECTIONS.languages}.${APPLICATION.nativeLanguage}`}
          />
        </Grid2>
        <Grid2 size={12}>
          <Alert variant="outlined" severity="info">
            <Typography variant="body2">Please add below any languages you have studied along with the number of years you have studied them for</Typography>
          </Alert>
        </Grid2>
        <LanguagesList control={control} />
      </TitledSection>
    </Stack>
  );
};

const getLanguageFieldPath = (index: number, key: (typeof LANGUAGE)[keyof typeof LANGUAGE]) =>
  `${SCHOOL_LANGUAGES_PAGE_SECTIONS.languages}.${PUT_APPLICATION.languageListPUT}[${index}].${key}` as FieldPath<SchoolAndLanguagesFormValues>;

const LanguagesList = ({ control }: { control: Control<SchoolAndLanguagesFormValues> }) => {
  const { fields, append, remove } = useFieldArray({ name: `${SCHOOL_LANGUAGES_PAGE_SECTIONS.languages}.${PUT_APPLICATION.languageListPUT}`, control });

  return (
    <>
      {fields?.map((lang, index) => (
        <Paper key={lang.id} sx={{ p: 2, width: '100%' }}>
          <Grid2 container columnGap={3} rowGap={{ xs: 2, md: 0 }}>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormField control={control} label="Language" type={FormFieldType.Text} name={getLanguageFieldPath(index, LANGUAGE.language)} />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4 }}>
              <FormField
                control={control}
                label="Number of Years"
                type={FormFieldType.Number}
                name={getLanguageFieldPath(index, LANGUAGE.numberOfYears)}
                textSuffix="years"
              />
            </Grid2>
            <Grid2 size={12}>
              <Button variant="outlined" color="error" onClick={() => remove(index)}>
                Delete
              </Button>
            </Grid2>
          </Grid2>
        </Paper>
      ))}
      <Grid2 size={12}>
        <Button startIcon={<AddIcon />} variant="outlined" color="primary" onClick={() => append({ [LANGUAGE.language]: '', [LANGUAGE.numberOfYears]: '' })}>
          Add a language
        </Button>
      </Grid2>
    </>
  );
};
